import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, isRef as _isRef, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { key: 2 }
const _hoisted_3 = { key: 3 }

import { defineAsyncComponent, onMounted, ref, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { accountService } from '@exchange/libs/account/service/src';
import { balanceService } from '@exchange/libs/balances/service/src';
import ConfirmJurisdictionModal from '@exchange/libs/jurisdiction/ConfirmJurisdictionModal.vue';
import RestrictedJurisdictionModal from '@exchange/libs/jurisdiction/RestrictedJurisdictionModal.vue';
import { modalVariant } from '@exchange/libs/modals/src';
import PublicRest from '@exchange/libs/rest-api/public-api';
import { useInitTransaction } from '@exchange/libs/transactions/funds/service/src';
import fundsService from '@exchange/libs/transactions/funds/service/src/lib/funds.service';
import { AccountIsNotMain, CoinSelect, FetchBitpandaUser, UserBlocked, UserNotVerified, TransactionModal } from '@exchange/libs/transactions/shared/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import modalService from '../../../../modals/src/lib/modals.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'Deposit',
  setup(__props) {

const DepositCrypto = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-deposit-crypto' */ './DepositCrypto.vue'));
const DepositFiat = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-deposit-fiat' */ './DepositFiat.vue'));
const DepositFiatCard = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-deposit-fiat-card' */ './DepositFiatCard.vue'));

const DepositMethod = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-deposit-method' */ './DepositMethod.vue'));

const router = useRouter();
const route = useRoute();

const { list: balances } = balanceService;

const cryptoDepositsEnabled = ref(false);
const fiatDepositsEnabled = ref(false);
const jurisdictionFiatRestricted = ref(false);

const isCardPaymentEnabled = launchdarkly.flags['is-card-payment-enabled'].value;

const variant = modalVariant.dark;

const {
  currenciesToShow,
  defaultFiatCurrency,
  filterCurrencyList,
  getCurrencyFromRoute,
  isBlocked,
  isVerified,
  isMainAccount,
  isRetailWithMissingAml,
  resetSelectedCurrency,
  selectedCurrency,
  selectedCurrencyIsCrypto,
  verification,
  search,
  selectedCurrencyType,
} = useInitTransaction();

const selectedDepositMethod = ref('');
selectedCurrencyType.value = null;

const handleMethodSelected = (method: string) => {
  selectedDepositMethod.value = method;
  if (selectedDepositMethod.value === 'crypto') {
    selectedCurrencyType.value = 'crypto';
  } else {
    selectedCurrencyType.value = 'fiat';
  }
};

const handleChangeMethod = () => {
  selectedDepositMethod.value = '';
  selectedCurrencyType.value = null;
  search.value = '';
};

const showRestrictedJurisdictionModal = async () => {
  modalService.show(
    shallowRef(RestrictedJurisdictionModal),
    {
      variant: modalVariant.dark,
      transactionType: 'deposit',
    },
    {
      'action-clicked': () => modalService.hide(),
    },
    {
      canClose: false,
    },
  );
};

const showConfirmJurisdictionModal = async () => {
  modalService.show(
    shallowRef(ConfirmJurisdictionModal),
    {
      variant: modalVariant.dark,
      transactionType: 'deposit',
    },
    {
      'action-clicked': (code) => {
        modalService.hide();
        window.location.reload();
      },
    },
    {
      canClose: false,
    },
  );
};

const checkUserResidence = async () => {
  const user = await accountService.awaitAccountUser();
  const identity = await accountService.checkIdentity();
  const jurisdictions = await PublicRest.Jurisdictions.get();

  if (!identity?.residency) {
    await showConfirmJurisdictionModal();
  }

  if (jurisdictions[identity.residency]) {
    const jurisdiction = jurisdictions[identity.residency];
    if (jurisdiction.noFiatTransfers) {
      jurisdictionFiatRestricted.value = true;
      fiatDepositsEnabled.value = false;
      if (!selectedCurrencyIsCrypto.value) {
        await showRestrictedJurisdictionModal();
      }
      selectedDepositMethod.value = 'crypto';
      selectedCurrencyType.value = 'crypto';
    }
  }
};

(async () => {
  await getCurrencyFromRoute(route, router);
  await checkUserResidence();
})();

const initializeDeposit = async () => {
  try {
    const accountHolderDetails = await fundsService.accountHolder.get();

    cryptoDepositsEnabled.value = accountHolderDetails?.enabled_crypto_deposits ?? true;
    fiatDepositsEnabled.value = !jurisdictionFiatRestricted.value && (accountHolderDetails?.enabled_fiat_deposits ?? true);

    if (!selectedDepositMethod.value) {
      if (cryptoDepositsEnabled.value && !fiatDepositsEnabled.value) {
        selectedDepositMethod.value = 'crypto';
        selectedCurrencyType.value = 'crypto';
      } else if (isCardPaymentEnabled && fiatDepositsEnabled.value) {
        selectedDepositMethod.value = 'card';
        selectedCurrencyType.value = 'fiat';
      }
    }
  } catch (e) {
    logger.error('Unable to get account holder details', e);
  }
};

onMounted(initializeDeposit);

return (_ctx: any,_cache: any) => {
  const _component_ot_banner = _resolveComponent("ot-banner")!
  const _component_ot_search = _resolveComponent("ot-search")!

  return (_openBlock(), _createBlock(_unref(TransactionModal), {
    class: "deposit-modal",
    name: "deposit",
    variant: _unref(variant),
    title: _ctx.$t('fundamentals.addFunds')
  }, {
    content: _withCtx(({ clear, searchClass, coinSelectClass, contentClass }) => [
      _createVNode(_unref(FetchBitpandaUser), { variant: _unref(variant) }, {
        default: _withCtx(() => [
          (jurisdictionFiatRestricted.value)
            ? (_openBlock(), _createBlock(_component_ot_banner, {
                key: 0,
                class: "deposit-modal__banner",
                variant: "warning"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('modules.transactions.withdraw.restrictedJurisdictionModal.depositMessage')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (!selectedDepositMethod.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(DepositMethod), {
                  "crypto-deposits-enabled": cryptoDepositsEnabled.value,
                  "fiat-deposits-enabled": fiatDepositsEnabled.value,
                  onMethodSelected: handleMethodSelected
                }, null, 8, ["crypto-deposits-enabled", "fiat-deposits-enabled"])
              ]))
            : _createCommentVNode("", true),
          (fiatDepositsEnabled.value && selectedDepositMethod.value === 'card')
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (!_unref(isBlocked) && _unref(isVerified) && !_unref(isRetailWithMissingAml))
                  ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(DepositFiatCard)), {
                      key: 0,
                      variant: _unref(variant),
                      "currency-options": _unref(currenciesToShow),
                      "deposit-method": selectedDepositMethod.value,
                      class: _normalizeClass(contentClass),
                      onClose: clear,
                      onChangeMethod: handleChangeMethod
                    }, null, 40, ["variant", "currency-options", "deposit-method", "class", "onClose"]))
                  : _createCommentVNode("", true)
              ]))
            : (selectedDepositMethod.value && selectedDepositMethod.value !== 'card')
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["deposit-modal__search", searchClass])
                  }, [
                    _createVNode(_component_ot_search, {
                      value: _unref(search),
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (_isRef(search) ? (search).value = $event : null)),
                      variant: _unref(variant),
                      "full-width": true,
                      "on-search-cb": _unref(filterCurrencyList),
                      onInputClick: _unref(resetSelectedCurrency)
                    }, null, 8, ["value", "variant", "on-search-cb", "onInputClick"])
                  ], 2),
                  (!_unref(isMainAccount))
                    ? (_openBlock(), _createBlock(_unref(AccountIsNotMain), {
                        key: 0,
                        operation: "deposit",
                        variant: _unref(variant),
                        class: _normalizeClass(contentClass),
                        onClose: clear
                      }, null, 8, ["variant", "class", "onClose"]))
                    : (_openBlock(), _createBlock(_unref(CoinSelect), {
                        key: 1,
                        selectedCurrency: _unref(selectedCurrency),
                        "onUpdate:selectedCurrency": _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectedCurrency) ? (selectedCurrency).value = $event : null)),
                        type: "deposit",
                        variant: _unref(variant),
                        class: _normalizeClass(coinSelectClass),
                        currencies: _unref(currenciesToShow),
                        "default-fiat-currency": _unref(defaultFiatCurrency),
                        balances: _unref(balances)
                      }, null, 8, ["selectedCurrency", "variant", "class", "currencies", "default-fiat-currency", "balances"]))
                ]))
              : _createCommentVNode("", true),
          (selectedDepositMethod.value && selectedDepositMethod.value !== 'card' && _unref(selectedCurrency) && !_unref(isBlocked) && _unref(isVerified) && !_unref(isRetailWithMissingAml))
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(selectedCurrencyIsCrypto) ? _unref(DepositCrypto) : _unref(DepositFiat)), {
                key: _unref(selectedCurrency).id,
                variant: _unref(variant),
                currency: _unref(selectedCurrency),
                "deposit-method": selectedDepositMethod.value,
                class: _normalizeClass(contentClass),
                onClose: clear
              }, null, 40, ["variant", "currency", "deposit-method", "class", "onClose"]))
            : (_unref(selectedCurrency) && _unref(isBlocked))
              ? (_openBlock(), _createBlock(_unref(UserBlocked), {
                  key: 5,
                  class: _normalizeClass(contentClass),
                  variant: _unref(variant),
                  onClose: clear
                }, null, 8, ["class", "variant", "onClose"]))
              : (_unref(selectedCurrency) && (!_unref(isVerified) || _unref(isRetailWithMissingAml)))
                ? (_openBlock(), _createBlock(_unref(UserNotVerified), {
                    key: 6,
                    variant: _unref(variant),
                    verification: _unref(verification),
                    "currency-symbol": _unref(selectedCurrency).id,
                    class: _normalizeClass(contentClass),
                    "text-key": "modules.transactions.verification.commonText",
                    "link-text-key": "modules.transactions.verification.button"
                  }, null, 8, ["variant", "verification", "currency-symbol", "class"]))
                : _createCommentVNode("", true)
        ]),
        _: 2
      }, 1032, ["variant"])
    ]),
    _: 1
  }, 8, ["variant", "title"]))
}
}

})